import {
  Dropdown,
  IDropdownOption,
  IDropdownProps,
  SelectableOptionMenuItemType,
} from 'office-ui-fabric-react'
import React, { useMemo } from 'react'
import { ChildProps, graphql } from 'react-apollo'
import {
  getAllVideoCollectionSummaryQuery,
  VideoCollectionSummaryResponse,
  VideoCollectionSummary,
} from '../../Api/videoAssets'

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>
type InputProps = Omit<IDropdownProps, 'options' | 'onChange'> & {
  value: string | undefined
  onChange: (videoCollectionId: string | undefined) => void
}

const withGql = graphql<InputProps, VideoCollectionSummaryResponse>(
  getAllVideoCollectionSummaryQuery,
  {
    options: () => ({ fetchPolicy: 'network-only' }),
  }
)

const NONE_KEY = 'none'

export const VideoCollectionDropdown = withGql(
  ({
    value,
    onChange,
    data,
    ...dropdownProps
  }: ChildProps<InputProps, VideoCollectionSummaryResponse>): JSX.Element => {
    const videoCollectionsSummaries =
      data && data.videoCollections ? data.videoCollections : []
    const options = useOptions({ videoCollectionsSummaries })
    let selectedKey = value || NONE_KEY
    if (!videoCollectionsSummaries.find((v) => v.id === value)) {
      selectedKey = NONE_KEY
    }
    return (
      <Dropdown
        {...dropdownProps}
        selectedKey={selectedKey}
        options={options}
        onChange={(
          _event: React.FormEvent<HTMLDivElement>,
          item: IDropdownOption | undefined
        ): void => {
          if (!item) {
            return
          }
          const videoCollectionId =
            item.key === NONE_KEY ? undefined : String(item.key)
          onChange(videoCollectionId)
        }}
      />
    )
  }
)

const useOptions = ({
  videoCollectionsSummaries,
}: {
  videoCollectionsSummaries: VideoCollectionSummary[]
}) => {
  return useMemo(() => {
    const noneOption: IDropdownOption = {
      key: NONE_KEY,
      text: 'None',
    }
    const ownOptionsHeader: IDropdownOption = {
      key: 'own',
      text: 'Own Video Content Collections',
      itemType: SelectableOptionMenuItemType.Header,
    }
    const sharedOptionsHeader: IDropdownOption = {
      key: 'shared',
      text: 'Shared Video Content Collections',
      itemType: SelectableOptionMenuItemType.Header,
    }
    const toOption = (
      videoCollectionSummary: VideoCollectionSummary
    ): IDropdownOption => ({
      key: videoCollectionSummary.id,
      text: videoCollectionSummary.title,
    })
    const ownOptions = videoCollectionsSummaries
      .filter(({ shared }) => !shared)
      .map(toOption)
    const sharedOptions = videoCollectionsSummaries
      .filter(({ shared }) => shared)
      .map(toOption)
    const options = [noneOption, ...ownOptions]
    if (sharedOptions.length) {
      options.unshift(ownOptionsHeader)
      options.push(sharedOptionsHeader, ...sharedOptions)
    }
    return options
  }, [videoCollectionsSummaries])
}
